<template>
  <div class="cly-simple-table">
    <div
      v-if="displaySearch || hasHeaderSlot"
      class="flex items-center gap-2 mb-3"
    >
      <div
        v-if="hasHeaderSlot"
        class="flex items-center gap-2"
      >
        <slot name="header-left" />
      </div>
      <div class="flex-1" />
      <div
        v-if="displaySearch"
        class="w-[260px]"
      >
        <input
          v-model="searchQuery"
          type="text"
          class="cly-simple-input"
          :placeholder="searchPlaceholder || $t('common.search')"
          :aria-label="searchPlaceholder || $t('common.search')"
        >
      </div>
    </div>

    <div class="cly-simple-table__wrap">
      <table class="cly-simple-table__table">
        <thead>
          <tr>
            <th
              v-for="col in columns"
              :key="col.key"
              :class="[
                'cly-simple-table__th',
                col.align === 'right' && 'text-right',
                col.sortable && 'cly-simple-table__th--sortable',
              ]"
              :style="col.minWidth ? `min-width: ${col.minWidth}px` : ''"
              @click="col.sortable ? toggleSort(col.key) : null"
            >
              <span class="inline-flex items-center gap-1">
                {{ col.label }}
                <span
                  v-if="col.sortable"
                  class="cly-simple-table__sort-arrow"
                >
                  <span :class="['block', sort.field === col.key && sort.order === 'asc' ? 'text-[#0a0a0a]' : 'text-[#d4d4d4]']">▲</span>
                  <span :class="['block -mt-1', sort.field === col.key && sort.order === 'desc' ? 'text-[#0a0a0a]' : 'text-[#d4d4d4]']">▼</span>
                </span>
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-if="showSkeleton">
            <tr
              v-for="n in skeletonRows"
              :key="`sk-${n}`"
              class="cly-simple-table__row"
            >
              <td
                v-for="col in columns"
                :key="col.key"
                class="cly-simple-table__td"
              >
                <span class="cly-simple-table__skeleton" />
              </td>
            </tr>
          </template>
          <template v-else>
            <tr
              v-for="(row, rowIdx) in displayedRows"
              :key="row._id || rowIdx"
              :class="[
                'cly-simple-table__row',
                (rowClickable || rowHref) && 'cly-simple-table__row--clickable',
              ]"
              @click="onRowClick(row, $event)"
            >
              <td
                v-for="col in columns"
                :key="col.key"
                :class="['cly-simple-table__td', col.align === 'right' && 'text-right']"
              >
                <slot
                  :name="`col-${col.key}`"
                  :row="row"
                >
                  {{ row[col.key] || '-' }}
                </slot>
              </td>
            </tr>
            <tr v-if="!displayedRows.length">
              <td
                :colspan="columns.length"
                class="cly-simple-table__empty"
              >
                <slot name="empty">
                  {{ $t('common.table.no-data') }}
                </slot>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <div
      v-if="!hideFooter && totalRows > 0"
      class="cly-simple-table__footer"
    >
      <div class="text-xs text-neutral-500">
        {{ rangeText }}
      </div>
      <div class="flex items-center gap-3">
        <button
          type="button"
          class="cly-simple-table__page-btn"
          :disabled="page <= 1"
          @click="goPrev"
        >
          ←
        </button>
        <span class="text-xs text-neutral-500">
          {{ page }} / {{ totalPages || 1 }}
        </span>
        <button
          type="button"
          class="cly-simple-table__page-btn"
          :disabled="page >= totalPages"
          @click="goNext"
        >
          →
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash';

export default {
  name: 'ClySimpleTable',
  props: {
    columns: {
      type: Array,
      required: true,
    },
    rows: {
      type: Array,
      default: () => [],
    },
    dataSource: {
      type: Object,
      default: null,
    },
    displaySearch: {
      type: Boolean,
      default: true,
    },
    searchPlaceholder: {
      type: String,
      default: '',
    },
    perPage: {
      type: Number,
      default: 10,
    },
    defaultSort: {
      type: Object,
      default: () => ({ field: '', order: 'asc' }),
    },
    hideFooter: {
      type: Boolean,
      default: false,
    },
    /** If provided, clicking a row calls this handler. */
    rowClick: {
      type: Function,
      default: null,
    },
    /**
     * If provided, clicking a row navigates to router-location(row).
     * Takes the row, returns a vue-router location descriptor (object or string).
     */
    rowHref: {
      type: Function,
      default: null,
    },
    /** Persist search/sort/page in URL ?q= &page= &sort=. Requires urlKey. */
    urlSync: {
      type: Boolean,
      default: false,
    },
    /** Unique short key to namespace URL params (e.g. "u", "c"). Only used when urlSync. */
    urlKey: {
      type: String,
      default: '',
    },
    /** Skeleton rows shown during initial load. Set 0 to disable. */
    skeletonRows: {
      type: Number,
      default: 6,
    },
  },
  data() {
    const urlState = this.urlSync ? this.readUrlState() : {};
    return {
      searchQuery: urlState.q || '',
      page: urlState.page || 1,
      sort: urlState.sort || {
        field: this.defaultSort.field || '',
        order: this.defaultSort.order || 'asc',
      },
      didInitialLoad: false,
    };
  },
  computed: {
    rowClickable() {
      return typeof this.rowClick === 'function';
    },
    hasHeaderSlot() {
      return !!this.$scopedSlots['header-left'] || !!this.$slots['header-left'];
    },
    isLoading() {
      if (this.dataSource) return !!this.dataSource.isLoading?.value;
      return false;
    },
    showSkeleton() {
      return this.skeletonRows > 0
        && this.isLoading
        && !this.didInitialLoad
        && !this.displayedRows.length;
    },
    remotePayload() {
      if (!this.dataSource) return null;
      const v = this.dataSource.rows?.value;
      if (v && Array.isArray(v.rows)) return v;
      return { rows: [], totalRows: 0, notFilteredTotalRows: 0 };
    },
    filteredLocalRows() {
      if (this.dataSource) return [];
      let arr = this.rows.slice();
      if (this.displaySearch && this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        arr = arr.filter((item) => Object.keys(item).some((k) => {
          const v = item[k];
          if (v === null || v === undefined) return false;
          return String(v).toLowerCase().indexOf(q) > -1;
        }));
      }
      if (this.sort.field) {
        const dir = this.sort.order === 'asc' ? 1 : -1;
        arr = arr.slice().sort((a, b) => {
          const va = a[this.sort.field];
          const vb = b[this.sort.field];
          if (va < vb) return -dir;
          if (va > vb) return dir;
          return 0;
        });
      }
      return arr;
    },
    displayedRows() {
      if (this.dataSource) return this.remotePayload.rows;
      const start = (this.page - 1) * this.perPage;
      return this.filteredLocalRows.slice(start, start + this.perPage);
    },
    totalRows() {
      if (this.dataSource) return this.remotePayload.totalRows || 0;
      return this.filteredLocalRows.length;
    },
    notFilteredTotalRows() {
      if (this.dataSource) return this.remotePayload.notFilteredTotalRows || 0;
      return this.rows.length;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.totalRows / this.perPage));
    },
    rangeText() {
      if (this.totalRows === 0) return this.$t('common.table.no-data');
      const start = (this.page - 1) * this.perPage + 1;
      const end = Math.min(start + this.perPage - 1, this.totalRows);
      let text = this.$t('common.showing')
        .replace('_START_', start)
        .replace('_END_', end)
        .replace('_TOTAL_', this.totalRows);
      if (this.searchQuery && this.notFilteredTotalRows > this.totalRows) {
        text += ` ${this.$t('common.filtered').replace('_MAX_', this.notFilteredTotalRows)}`;
      }
      return text;
    },
    controlParams() {
      return {
        searchQuery: this.searchQuery,
        page: this.page,
        perPage: this.perPage,
        sort: this.sort.field ? [{ field: this.sort.field, type: this.sort.order }] : [],
      };
    },
    urlNamespace() {
      return this.urlKey ? `${this.urlKey}_` : '';
    },
  },
  watch: {
    searchQuery() {
      this.page = 1;
      this.debouncedFetch();
      if (this.urlSync) this.writeUrlState();
    },
    sort: {
      deep: true,
      handler() {
        this.debouncedFetch();
        if (this.urlSync) this.writeUrlState();
      },
    },
    page() {
      this.debouncedFetch();
      if (this.urlSync) this.writeUrlState();
    },
    isLoading(val) {
      if (!val) this.didInitialLoad = true;
    },
  },
  created() {
    this.debouncedFetch = debounce(this.triggerFetch, 250);
  },
  mounted() {
    this.triggerFetch();
  },
  methods: {
    triggerFetch() {
      if (!this.dataSource || !this.dataSource.fetch) return;
      this.dataSource.fetch(this.controlParams);
    },
    toggleSort(field) {
      if (this.sort.field !== field) {
        this.sort = { field, order: 'asc' };
      } else if (this.sort.order === 'asc') {
        this.sort = { field, order: 'desc' };
      } else {
        this.sort = { field: '', order: 'asc' };
      }
    },
    goPrev() {
      if (this.page > 1) this.page -= 1;
    },
    goNext() {
      if (this.page < this.totalPages) this.page += 1;
    },
    onRowClick(row, event) {
      if (event.target.closest('a, button, input, select, textarea, [data-stop-row-click]')) return;
      if (typeof this.rowClick === 'function') {
        this.rowClick(row);
        return;
      }
      if (typeof this.rowHref === 'function') {
        const to = this.rowHref(row);
        if (to) this.$router.push(to);
      }
    },
    readUrlState() {
      if (!this.$route) return {};
      const ns = this.urlKey ? `${this.urlKey}_` : '';
      const q = this.$route.query[`${ns}q`];
      const page = parseInt(this.$route.query[`${ns}page`], 10);
      const sortRaw = this.$route.query[`${ns}sort`];
      const sort = sortRaw && typeof sortRaw === 'string' && sortRaw.includes(':')
        ? { field: sortRaw.split(':')[0], order: sortRaw.split(':')[1] === 'desc' ? 'desc' : 'asc' }
        : undefined;
      return {
        q: typeof q === 'string' ? q : '',
        page: Number.isFinite(page) && page > 0 ? page : 1,
        sort,
      };
    },
    writeUrlState() {
      if (!this.$route || !this.$router) return;
      const ns = this.urlNamespace;
      const query = { ...this.$route.query };
      if (this.searchQuery) query[`${ns}q`] = this.searchQuery; else delete query[`${ns}q`];
      if (this.page > 1) query[`${ns}page`] = String(this.page); else delete query[`${ns}page`];
      if (this.sort.field) query[`${ns}sort`] = `${this.sort.field}:${this.sort.order}`;
      else delete query[`${ns}sort`];
      if (JSON.stringify(query) === JSON.stringify(this.$route.query)) return;
      this.$router.replace({ query }).catch(() => {});
    },
  },
};
</script>

<style>
.cly-simple-table__wrap {
  border: 1px solid #EBEBEB;
  border-radius: 6px;
  background: #FFFFFF;
  overflow-x: auto;
}

.cly-simple-table__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  color: #0a0a0a;
}

.cly-simple-table__th {
  text-align: left;
  font-weight: 500;
  font-size: 11px;
  color: #737373;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 12px 16px;
  border-bottom: 1px solid #EBEBEB;
  background: #FAFAFA;
  white-space: nowrap;
}

.cly-simple-table__th--sortable {
  cursor: pointer;
  user-select: none;
}

.cly-simple-table__th--sortable:hover {
  color: #0a0a0a;
}

.cly-simple-table__sort-arrow {
  display: inline-flex;
  flex-direction: column;
  font-size: 8px;
  line-height: 1;
  margin-left: 2px;
}

.cly-simple-table__row {
  border-bottom: 1px solid #F5F5F5;
  transition: background 80ms ease;
}

.cly-simple-table__row:last-child {
  border-bottom: none;
}

.cly-simple-table__row--clickable {
  cursor: pointer;
}

.cly-simple-table__row--clickable:hover {
  background: #FAFAFA;
}

.cly-simple-table__td {
  padding: 12px 16px;
  vertical-align: middle;
}

.cly-simple-table__empty {
  padding: 48px 16px;
  text-align: center;
  color: #a3a3a3;
  font-size: 13px;
}

.cly-simple-table__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 4px;
}

.cly-simple-table__page-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid #EBEBEB;
  border-radius: 6px;
  background: #FFFFFF;
  color: #0a0a0a;
  font-size: 14px;
  cursor: pointer;
}

.cly-simple-table__page-btn:hover:not(:disabled) {
  border-color: #a3a3a3;
}

.cly-simple-table__page-btn:disabled {
  color: #d4d4d4;
  cursor: not-allowed;
}

.cly-simple-table__skeleton {
  display: block;
  height: 12px;
  width: 70%;
  background: linear-gradient(90deg, #F5F5F5 0%, #EBEBEB 50%, #F5F5F5 100%);
  background-size: 200% 100%;
  border-radius: 4px;
  animation: cly-skeleton 1.4s ease-in-out infinite;
}

@keyframes cly-skeleton {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.cly-simple-input {
  width: 100%;
  height: 40px;
  line-height: 40px;
  border-radius: 6px;
  border: 1px solid #EBEBEB;
  background-color: #FFFFFF;
  font-size: 14px;
  color: #0a0a0a;
  padding: 0 12px;
  outline: none;
  transition: border-color 120ms ease, box-shadow 120ms ease;
}

.cly-simple-input::placeholder {
  color: #a3a3a3;
}

.cly-simple-input:focus {
  border-color: #1EAB56;
  box-shadow: 0 0 0 2px rgba(30, 171, 86, 0.15);
}
</style>
